import type { TrackingFn } from '@change-corgi/core/react/tracking';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import { getWindow } from '@change-corgi/core/window';
import type { ModalHookResult } from '@change-corgi/design-system-legacy/modals';

import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { trackShare, trackShareFailure } from './track';

type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: Record<string, string>;
	openTiktokEducationModal: ModalHookResult<
		{ onContinueAction: () => Promise<void> },
		{ successfulShare: boolean } | undefined
	>;
};

export async function shareTiktok(
	data: { url: string; image: File | null },
	{ trackingEvents, trackingData, openTiktokEducationModal }: Options,
	track: TrackingFn,
	i18nContext: UtilityContext['i18n'],
): Promise<boolean> {
	const { url, image } = data;

	if (!image) {
		trackShare(trackingEvents.cancel, trackingData, track);
		return false;
	}

	const { navigator } = getWindow();

	const triggerNativeShare = async () => {
		try {
			await copyToClipboard(url, i18nContext);
			await navigator.share({ files: [image] });
			trackShare(trackingEvents.success, trackingData, track);
		} catch (e) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const error = e as Error;
			trackShareFailure(
				trackingEvents.failure,
				{
					...trackingData,
					errorName: error.name,
					errorMessage: error.message,
				},
				track,
			);
		}
	};

	trackShare(trackingEvents.intent, trackingData, track);

	const successfulShare = await (async () => {
		try {
			if (!navigator.canShare({ url, files: [image] })) {
				return false;
			}
			trackShare(trackingEvents.success, trackingData, track);
			const modalResult = await openTiktokEducationModal({ onContinueAction: triggerNativeShare }).waitForResult();

			return !!modalResult?.successfulShare;
		} catch (error) {
			return false;
		}
	})();

	if (successfulShare) {
		trackShare(trackingEvents.continue, trackingData, track);
	} else {
		trackShare(trackingEvents.cancel, trackingData, track);
	}

	return successfulShare;
}
